import { useEffect } from "react";
import { z } from "zod";
import { Form } from "../../../../../../../components/Form/Form.tsx";
import { useForm } from "../../../../../../../components/Form/FormContext.ts";
import { PanelLayout } from "../../../../../components/Layout/PanelLayout.tsx";
import { ActionsBar } from "../../../../../components/Panel/Settings/ActionsBar.tsx";
import { PromptSection } from "../../../../../components/Panel/Settings/PromptSection.tsx";
import { useBoardUuid } from "../../../../../hooks/useBoardUuid.ts";
import { useSelectedAsset } from "../../../../../hooks/useSelectedAsset.ts";
import { scrollToEditionHistoryStore } from "../../../../../stores/scrollToEditionHistoryStore.ts";
import { getErrorsFiltered } from "../../../../../utils/getErrorsFiltered.ts";
import { usePromptWarnings } from "../../../../hooks/usePromptWarnings.ts";
import { useEditImageWithPrompt } from "./hooks/useEditImageWithPrompt.ts";
import { useEditImageWithPromptSettings } from "./hooks/useEditImageWithPromptSettings.ts";

export const EditImageWithPromptSettings = () => {
  const { mutation: editImageWithPrompt, isLoading: isGenerationLoading } =
    useEditImageWithPrompt({
      onSuccess: () => {
        scrollToEditionHistoryStore.triggerScrollToEditionHistory();
      },
    });

  return (
    <Form
      schema={zEditImageWithPromptSettings}
      initialValues={{
        selectedAssetUuid: "",
        prompt: "",
        isSelectedImageSucceeded: false,
      }}
      onSubmit={(values) => {
        // FIXME: make the mutation async
        editImageWithPrompt({
          assetUuid: values.selectedAssetUuid,
        });
        return Promise.resolve(true);
      }}
    >
      <EditImageWithPromptSettingsFormContent
        isGenerationLoading={isGenerationLoading}
      />
    </Form>
  );
};

const zEditImageWithPromptSettings = z.object({
  selectedAssetUuid: z
    .string({ required_error: "Please upload an image in the canvas" })
    .uuid(),
  prompt: z.string().min(1, { message: "Please enter a description" }),
  isSelectedImageSucceeded: z.boolean().refine((val) => val, {
    message: "Please wait for your image to be generated",
  }),
});
type EditImageWithPromptSettingsValues = z.input<
  typeof zEditImageWithPromptSettings
>;

export const EditImageWithPromptSettingsFormContent = ({
  isGenerationLoading,
}: {
  isGenerationLoading: boolean;
}) => {
  const boardUuid = useBoardUuid();
  const { settings, setSettings } = useEditImageWithPromptSettings(boardUuid);
  const { selectedAsset } = useSelectedAsset();

  const { setValues, submit, useError } =
    useForm<EditImageWithPromptSettingsValues>();
  const missingAssetError = useError((v) => v.selectedAssetUuid);
  let imageNotSucceededError = useError((v) => v.isSelectedImageSucceeded);
  if (missingAssetError) imageNotSucceededError = undefined;

  const promptEmptyError = useError((v) => v.prompt);

  useEffect(() => {
    setValues({ selectedAssetUuid: selectedAsset?.uuid });
  }, [setValues, selectedAsset?.uuid]);

  useEffect(() => {
    setValues({
      isSelectedImageSucceeded: selectedAsset?.content.status === "SUCCEEDED",
    });
  }, [setValues, selectedAsset?.content.status]);

  useEffect(() => {
    setValues({ prompt: settings.prompt });
  }, [setValues, settings.prompt]);

  const warnings = usePromptWarnings({
    prompt: settings.prompt,
    selectedAsset,
  });

  return (
    <PanelLayout
      bottomChildren={
        <ActionsBar
          isLoading={isGenerationLoading}
          onPrimaryActionClick={submit}
          primaryActionButtonContent="Edit"
          // FIXME: Intercom article should be updated to match new tool settings
          intercomArticle="get_variations_of_your_image"
          errors={getErrorsFiltered([
            missingAssetError,
            imageNotSucceededError,
          ])}
        />
      }
    >
      <PromptSection
        sectionTitle="Edit using prompt"
        prompt={settings.prompt}
        warnings={warnings}
        setPrompt={(newPrompt) => {
          setSettings({
            prompt: newPrompt,
          });
        }}
        placeholder="Describe what you want to edit"
        onEnterKeyDown={submit}
        errors={getErrorsFiltered([promptEmptyError])}
      />
    </PanelLayout>
  );
};
